import { Button, Group, NumberInput, Select, Stack, TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useProjectCostCodes } from "@/hooks/useCostCodes";
import { useCommitments } from "@/hooks/useCommitments";
import type { Actual } from "@/hooks/useActuals";
import { formatDateForStorage } from "@/lib/date-utils";

export interface ActualFormData {
  costCodeId: string;
  amount: number;
  description: string;
  date: string;
  commitmentId?: string;
}

interface ActualFormProps {
  onSubmit: (data: ActualFormData) => void;
  onCancel?: () => void;
  initialData?: Actual;
  loading?: boolean;
  submitText?: string;
  projectId: string;
}

export function ActualForm({
  onSubmit,
  onCancel,
  initialData,
  loading = false,
  submitText = "Save",
  projectId,
}: ActualFormProps) {
  const { data: projectCostCodesData } = useProjectCostCodes(projectId);
  const { data: commitmentsData } = useCommitments(projectId);

  const form = useForm({
    initialValues: {
      costCodeId: initialData?.costCodeId || "",
      amount: initialData?.amount || 0,
      description: initialData?.description ?? "",
      date: initialData?.date || formatDateForStorage(new Date()),
      commitmentId: initialData?.commitmentId || "",
    },
    validate: {
      costCodeId: (value) => (!value ? "Cost code is required" : null),
      amount: (value) => (value <= 0 ? "Amount must be greater than 0" : null),
      date: (value) => (!value ? "Date is required" : null),
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit({
      ...values,
      commitmentId: values.commitmentId || undefined,
    } as ActualFormData);
  });

  // Prepare cost code options from project cost codes
  const projectCostCodes = projectCostCodesData?.projectCostCodes ?? [];
  const costCodeOptions = projectCostCodes.map((pcc) => ({
    value: pcc.costCodeId,
    label: `${pcc.costCode?.code ?? "?"} - ${pcc.costCode?.name ?? "Unknown"}`,
    group: pcc.costCode?.division ?? "Uncategorized",
  }));

  // Prepare commitment options
  const commitments = commitmentsData?.commitments ?? [];
  const commitmentOptions = [
    { value: "", label: "None" },
    ...commitments.map((c) => ({
      value: c.id,
      label: `${c.vendorName} - ${c.costCode} - $${c.amount.toFixed(2)}`,
    })),
  ];

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Cost Code"
          placeholder="Select cost code"
          data={costCodeOptions}
          searchable
          required
          {...form.getInputProps("costCodeId")}
        />

        <NumberInput
          label="Amount"
          placeholder="0.00"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          required
          {...form.getInputProps("amount")}
        />

        <TextInput
          label="Date"
          type="date"
          required
          {...form.getInputProps("date")}
        />

        <Textarea
          label="Description"
          placeholder="Enter description or notes (optional)"
          rows={3}
          {...form.getInputProps("description")}
        />

        <Select
          label="Linked Commitment"
          placeholder="Select commitment (optional)"
          data={commitmentOptions}
          searchable
          clearable
          {...form.getInputProps("commitmentId")}
        />

        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={loading}>
            {submitText}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
